import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDownToLine, ShieldCheck, CalendarClock, MapPin } from "lucide-react";
import { scrollToId } from "@/lib/api";

const LINES = ["FROM GLOBAL", "SUPPLIERS TO", "U.S. CUSTOMERS."];

const STATS = [
  { value: "20,000", label: "Upcoming Loads" },
  { value: "$4.20", label: "Max Rate Per Mile" },
  { value: "3", label: "Equipment Classes" },
  { value: "Minutes", label: "Verification Time" },
];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const yGlow = useTransform(scrollYProgress, [0, 1], [0, 60]);

  return (
    <section ref={ref} className="relative overflow-hidden bg-[#070F1E] pt-32 lg:pt-40">
      {/* ambient glows */}
      <motion.div style={{ y: yGlow }} className="pointer-events-none absolute -top-40 left-1/2 h-[560px] w-[900px] -translate-x-1/2 rounded-full bg-[#0066FF]/20 blur-[140px]" />
      <div className="pointer-events-none absolute bottom-0 right-0 h-[400px] w-[500px] rounded-full bg-[#00F0FF]/10 blur-[120px]" />

      <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-5 pb-16 lg:grid-cols-[1.1fr_0.9fr] lg:px-8 lg:pb-24">
        <div>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="mb-6 inline-flex items-center gap-2 rounded-full border border-[#0066FF]/30 bg-[#0066FF]/10 px-4 py-1.5 font-mono text-xs uppercase tracking-[0.25em] text-[#00F0FF]"
          >
            <MapPin className="h-3.5 w-3.5" />
            Worldwide Supply & Import Logistics
          </motion.div>

          <h1 className="font-display text-[1.9rem] font-extrabold leading-[1.06] tracking-tight sm:text-5xl lg:text-[4.2rem]">
            {LINES.map((line, i) => (
              <span key={line} className="block overflow-hidden pb-1">
                <motion.span
                  className="block"
                  initial={{ y: "112%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.85, delay: 0.1 + i * 0.12, ease: [0.22, 1, 0.36, 1] }}
                >
                  {i === 2 ? <span className="text-gradient-blue">{line}</span> : line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.55, ease: [0.22, 1, 0.36, 1] }}
            className="mt-6 max-w-xl text-base leading-relaxed text-slate-400 sm:text-lg"
          >
            PORTBRIDGE Global moves freight from worldwide suppliers to U.S. customers through a
            fully verified carrier network — and our next initiative is our biggest yet.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-btn-download-portal"
              onClick={() => scrollToId("onboarding")}
              className="group flex items-center gap-2.5 rounded-full bg-[#0066FF] px-7 py-3.5 text-sm font-semibold text-white transition-all hover:bg-[#0052cc] hover:shadow-[0_0_40px_rgba(0,102,255,0.55)]"
            >
              <ArrowDownToLine className="h-4 w-4 transition-transform group-hover:translate-y-0.5" />
              Download Carrier Portal
            </button>
            <button
              data-testid="hero-btn-explore-loads"
              onClick={() => scrollToId("project")}
              className="rounded-full border border-slate-600 px-7 py-3.5 text-sm font-semibold text-slate-200 transition-colors hover:border-[#00F0FF] hover:text-[#00F0FF]"
            >
              View Upcoming 20,000 Loads
            </button>
          </motion.div>
        </div>

        <motion.div style={{ y: yImg }} className="relative hidden lg:block">
          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="relative overflow-hidden rounded-3xl border border-[#0066FF]/25 glow-blue"
          >
            <img
              src="https://images.unsplash.com/photo-1605745341112-85968b19335b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwyfHxjYXJnbyUyMHNoaXAlMjBjb250YWluZXIlMjBwb3J0JTIwbG9naXN0aWNzfGVufDB8fHx8MTc5MDYwNjYyMXww&ixlib=rb-4.1.0&q=85"
              alt="Ocean container cargo ship at sea"
              className="h-[460px] w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#070F1E]/80 via-transparent to-transparent" />
          </motion.div>

          <div className="float-soft absolute -left-8 top-10 flex items-center gap-2.5 rounded-xl border border-[#0066FF]/30 bg-[#0B172A]/90 px-4 py-3 shadow-xl backdrop-blur">
            <ShieldCheck className="h-5 w-5 text-[#00F0FF]" />
            <div>
              <p className="font-mono text-xs font-bold text-white">FMCSA · DOT</p>
              <p className="text-xs text-slate-400">Verified Network Only</p>
            </div>
          </div>

          <div className="float-soft-delayed absolute -right-6 bottom-12 flex items-center gap-2.5 rounded-xl border border-[#FF3B30]/40 bg-[#0B172A]/90 px-4 py-3 shadow-xl backdrop-blur">
            <CalendarClock className="h-5 w-5 text-[#FF3B30]" />
            <div>
              <p className="font-mono text-xs font-bold text-white">20,000 LOADS</p>
              <p className="text-xs text-slate-400">Starting November 1</p>
            </div>
          </div>
        </motion.div>
      </div>

      {/* stats band */}
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.85, ease: [0.22, 1, 0.36, 1] }}
        className="relative border-t border-[#0066FF]/15 bg-[#0B172A]/60"
      >
        <div className="mx-auto grid max-w-7xl grid-cols-2 gap-y-6 px-5 py-8 md:grid-cols-4 lg:px-8">
          {STATS.map((s) => (
            <div key={s.label} className="text-center lg:text-left">
              <p className="font-mono text-2xl font-extrabold text-white lg:text-3xl">{s.value}</p>
              <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">{s.label}</p>
            </div>
          ))}
        </div>
      </motion.div>
    </section>
  );
}
